import React, { useCallback, useContext, useEffect } from 'react'
import { fetchProductSubscription } from 'actions/LoyaltyActions'
import FormatCurrency from 'components/Common/FormatCurrency'
import Modal from 'components/Luxkit/Modal/Modal'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Group from 'components/utils/Group'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { LUXURY_PLUS, LUXURY_PLUS_PATHS } from 'loyalty/constants/base'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import SolidInfoCircleIcon from 'components/Luxkit/Icons/solid/SolidInfoCircleIcon'
import LuxuryPlusSubscriptionCancellationModal from '../LuxuryPlusSubscriptionCancellationModal'
import TextLink from 'components/Luxkit/TextLink'
import ModalContext from 'contexts/ModalContext'
import { getHotelOfferUrl } from 'lib/offer/offerPageURL'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import LuxuryPlusBenefitsList from '../LuxuryPlusBenefitsList'
import { formatCurrency } from 'lib/format/formatCurrencyIntl'
import GeoContext from 'contexts/geoContext'
import LineStarIcon from 'components/Luxkit/Icons/line/LineStarIcon'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import LineUsdCircleIcon from 'components/Luxkit/Icons/line/LineUsdCircleIcon'

interface Props {
  accommodationOffer?: App.Offer | App.OfferSummary
  saveAmount: number
}

function LuxPlusMemberPriceModal({ accommodationOffer, saveAmount }:Props) {
  const dispatch = useAppDispatch()
  const showModal = useContext(ModalContext)
  const { resolve } = useModalElementContext()

  const subscription = useAppSelector(state => state.loyalty.products.subscription)
  const offerUrl = accommodationOffer ? getHotelOfferUrl(accommodationOffer?.id, accommodationOffer?.type) : undefined
  const geo = useContext(GeoContext)
  const formattedSaveAmount = formatCurrency(saveAmount, geo.currentCurrency, geo.currentRegionCode) ?? 0

  const onShowCancellationPolicy = useCallback(() => {
    showModal(<LuxuryPlusSubscriptionCancellationModal offer={subscription.offers?.[0]} />)
  }, [showModal, subscription.offers])

  useEffect(() => {
    dispatch(fetchProductSubscription())
  }, [dispatch])

  return <Modal
    primaryActionText="View offer"
    isPrimaryActionHidden={!offerUrl}
    primaryActionTo={offerUrl}
    secondaryActionText={`Learn more about ${LUXURY_PLUS.PROGRAM_NAME}`}
    secondaryActionTo={`/${LUXURY_PLUS_PATHS.LANDING_PAGE}`}
    title={`Join ${LUXURY_PLUS.PROGRAM_NAME} to save ${formattedSaveAmount} on this offer and more!`}>
    {subscription.fetching && <LoadingIndicator />}
    {subscription.error && <BodyText variant="medium">There was an error loading the prices details. Please try again later.</BodyText>}
    {subscription.offers &&
      <Group direction="vertical" gap={24}>
        <Group direction="vertical" gap={12}>
          <BodyText variant="medium">
            Become a member of our VIP travel club for just <FormatCurrency value={subscription.offers[0].price} />/year to unlock instant benefits when you book {accommodationOffer ? <b>{accommodationOffer?.property?.name}</b> : 'this hotel'} today. Cancel anytime!
          </BodyText>
          <Group direction="vertical" gap={8}>
            <BodyTextBlock variant="medium" colour="highlight-secondary" weight="bold" startIcon={<LineUsdCircleIcon />}>
              Save {formattedSaveAmount} on this booking
            </BodyTextBlock>
            <BodyTextBlock variant="medium" colour="highlight-secondary" weight="bold" startIcon={<LineStarIcon />}>
              Special offer: we’ll waive the {LUXURY_PLUS.PROGRAM_NAME} joining fee, saving you <FormatCurrency format="rounded" value={subscription.offers[0].joinFee} />
            </BodyTextBlock>
          </Group>
          <BodyText variant="medium" weight="bold">
            You’ll also enjoy these unlimited benefits all year round:
          </BodyText>
          <LuxuryPlusBenefitsList benefits={subscription.offers[0].benefits} />
          <TextLink weight="bold" variant="highlight" size="medium" endIcon={<SolidInfoCircleIcon colour="neutral-one" />} onClick={onShowCancellationPolicy}>
            Cancel renewal anytime
          </TextLink>
        </Group>
        <BodyText variant="small">
          There is a one-off joining fee of $500, paid when you join the club. We’ll waive the joining fee with any hotel booking, saving you <FormatCurrency format="rounded" value={subscription.offers[0].joinFee} />. {LUXURY_PLUS.PROGRAM_NAME} annual membership renews on your sign-up anniversary date. We'll send you a reminder. You can cancel your renewal anytime. <TextLink onClick={() => resolve()} weight="regular" to={`/${LUXURY_PLUS_PATHS.TERMS_AND_CONDITIONS_PAGE}`}>T&Cs apply</TextLink>
        </BodyText>
      </Group>}
  </Modal>
}

export default LuxPlusMemberPriceModal
